import React from 'react';
import PropTypes from 'prop-types';

import { FormattedMessage } from 'react-intl';
import { Fields, reduxForm, change, stopAsyncValidation } from 'redux-form';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';

import {
  scrollToFirstError,
  generateValidation,
  formatPhoneNumber,
  removePhoneFormatting,
} from 'ecs-react-components/dist/index.es';
import { submitAddContact } from '../../../redux/modules/personal-contacts/personal-contacts-middleware';
import {
  loadRelationships,
  loadPreferredCommunications,
  loadCountries,
  loadStates,
} from '../../../redux/modules/lookups/lookups-middleware';
import { loadSupportedLanguages } from '../../../redux/modules/capability/capability-middleware';
import Message from '../../../containers/Message/Message';
import AddressContainer from './Address';
import { messages } from './Contact.i18n';
import ContactInformation from './ContactInformation';
import NameRelation from './NameRelation';
import Header from '../../../components/Header/Header';
import { validations } from './Contact.validations';
import { setIsDedicatedWorkflow } from '../../../redux/modules/workflow/workflow-action-creator';
import { loadPatientAddress } from '../../../redux/modules/patient/patient-middleware';
import AccountSettings from './AccountSettings';
import * as MsgBarActions from '../../../redux/modules/system-shared/message-bar-action-creator';
import { checkIfArInvitationExists } from '../../../redux/modules/ar-invitation/ar-invitation-middleware';
import { setPrevInvitationRoute } from '../../../redux/modules/ar-invitation/ar-invitation-action-creator';
import { loadLanguageTranslations } from '../../../redux/modules/language/language-middleware';

const fields = [
  'contactFirstName',
  'contactLastName',
  'relationshipId',
  'isPrimaryContact',
  'isNextOfKin',
  'providesTransport',
  'homePhone',
  'isOkToLeaveMsgHome',
  'mobilePhone',
  'isOkToLeaveMsgMobile',
  'workPhone',
  'isOkToLeaveMsgWork',
  'contactEmail',
  'preferredCommunicationId',
  'address1',
  'address2',
  'city',
  'zipCode',
  'state',
  'country',
  'usePatientAddress',
  'isActive',
  'communicationLanguageId',
];

class Contact extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      inviteContactChecked: false,
      stayAfterSave: false,
      saveFromAccountSetting: false,
    };
  }

  componentDidMount = () => {
    const {
      dispatch,
      patientId,
    } = this.props;

    if (!patientId) {
      return;
    }

    dispatch(setIsDedicatedWorkflow(true));
    dispatch(loadRelationships());
    dispatch(loadCountries());
    dispatch(loadStates());
    dispatch(loadPreferredCommunications());
    dispatch(loadSupportedLanguages());
    if (patientId) {
      dispatch(loadPatientAddress(patientId));
    }
    dispatch(loadLanguageTranslations(this.props.languageCode));
    if (this.props.isEditMode && this.props.contactValue) {
      this.props.dispatch(checkIfArInvitationExists(this.props.contactValue.contactId));
      this.props.dispatch(setPrevInvitationRoute('/edit-contact'));
    } else {
      this.props.dispatch(setPrevInvitationRoute('/dashboard'));
    }
  }

  componentWillUnmount = () => {
    this.props.dispatch(setIsDedicatedWorkflow(false));
  }

  onSubmit = (values, dispatch, callback) => {
    const {
      patientId,
      isEditMode,
      personalContacts,
      selectedContactId,
    } = this.props;
    const personalContact = {
      ...personalContacts.find((x) => x.contactId === selectedContactId),
      patientId,
      contactFirstName: values.contactFirstName.trim(),
      contactLastName: values.contactLastName.trim(),
      relationshipId: values.relationshipId,
      isPrimaryContact: values.isPrimaryContact ? values.isPrimaryContact : false,
      isNextOfKin: values.isNextOfKin ? values.isNextOfKin.checked : false,
      providesTransport: values.providesTransport ? values.providesTransport.checked : false,
      homePhone: removePhoneFormatting(values.homePhone),
      isOkToLeaveMsgHome: values.isOkToLeaveMsgHome ? values.isOkToLeaveMsgHome.checked : false,
      mobilePhone: removePhoneFormatting(values.mobilePhone),
      isOkToLeaveMsgMobile: values.isOkToLeaveMsgMobile ? values.isOkToLeaveMsgMobile.checked : false,
      workPhone: removePhoneFormatting(values.workPhone),
      isOkToLeaveMsgWork: values.isOkToLeaveMsgWork ? values.isOkToLeaveMsgWork.checked : false,
      contactEmail: values.contactEmail,
      preferredCommunicationId: values.preferredCommunicationId,
      address1: values.address1,
      address2: values.address2,
      city: values.city,
      zipCode: values.zipCode,
      state: values.state,
      country: values.country,
      usePatientAddress: values.usePatientAddress ? values.usePatientAddress.checked : false,
      isActive: true, // if value not set, API will default this to false
      communicationLanguageId: values.communicationLanguageId };

    if (!personalContact.contactEmail && this.state.inviteContactChecked) {
      this.props.dispatch(MsgBarActions.showErrorMessage('contactEmailRequiredForInvite'));
      return;
    }

    dispatch(submitAddContact(personalContact, isEditMode,
      this.state.inviteContactChecked, this.state.saveFromAccountSetting))
      .then(() => {
        this.props.onSubmitAddContact();
        if (this.state.inviteContactChecked) {
          this.props.history.push('/invite-auth-statement');
        } else if (this.state.stayAfterSave) {
          this.setState({ stayAfterSave: false });
          if (callback) {
            callback();
          }
        } else {
          this.props.history.push('/');
        }
      });
  }

  setAddressToPatientAddress = () => {
    const { patientAddress, dispatch } = this.props;

    dispatch(change('contactform', 'address1', patientAddress.address1));
    dispatch(change('contactform', 'address2', patientAddress.address2));
    dispatch(change('contactform', 'city', patientAddress.city));
    dispatch(change('contactform', 'zipCode', patientAddress.zipCode));
    dispatch(change('contactform', 'state', patientAddress.state));
    dispatch(change('contactform', 'country', patientAddress.country));
  }

  setPageTitle() {
    if (this.props.isEditMode) {
      return <FormattedMessage { ...messages.editContactPage.title } />;
    }
    return <FormattedMessage { ...messages.addContactPage.title } />;
  }

  getStateOptions = (fieldsMeta) => {
    const {
      states,
      patientAddress,
    } = this.props;

    const options = states;
    if (fieldsMeta.usePatientAddress.input.checked && patientAddress &&
        states.find((x) => x.stateNameDisplay === patientAddress.state) === undefined) {
      options.push({
        stateId: 0,
        countryId: 0,
        stateNameDisplay: patientAddress.state,
      });
    }

    return options;
  }

  getContactFormRef = (c) => { this.contactFormRef = c; };

  getCountryOptions = (fieldsMeta) => {
    const {
      countries,
      patientAddress,
    } = this.props;

    const options = countries;
    if (fieldsMeta.usePatientAddress.input.checked && patientAddress &&
        countries.find((x) => x.countryNameDisplay === patientAddress.country) === undefined) {
      options.push({
        sortOrder: 1000,
        countryId: 1000,
        countryNameDisplay: patientAddress && patientAddress.country,
      });
    }

    return options;
  }

  handleUsePatientAddressClick = (event) => {
    if (event.target.checked) {
      this.setAddressToPatientAddress();
    }
  }

  handleOnInviteContactChange = (event) => {
    this.setState({ inviteContactChecked: event.target.checked });
  }

  handleOnPhoneInputChange = (event) => {
    this.props.dispatch(
      change('contactform',
        event.target.name,
        formatPhoneNumber(event.target.value)));
  }

  handleSubmitClick = () => {
    this.props.dispatch(stopAsyncValidation('contactform', { clear: true }));
    this.props.dispatch(MsgBarActions.dismissMessage());
  }

  handleCancelClick = (event) => {
    event.preventDefault();
    this.props.history.push('/');
  }

  handleInputChange = (event) => {
    this.props.dispatch(
      change('contactform',
        event.target.name,
        event.target.value));
  }

  inviteContactEnabled = () => {
    const dashboard = this.props.capabilities.find((m) => m.moduleName === 'Dashboard');
    if (dashboard && dashboard.details) {
      const pc = dashboard.details.find((m) => m.fieldName === 'PersonalContacts');
      if (pc && pc.details) {
        const ic = pc.details.find((m) => m.fieldName === 'InviteContact');
        if (ic) {
          return true;
        }
      }
    }
    return false;
  }

  submitContactForm = (successCallback, errCallback) => {
    const isEmailConfirmed = (contact, values) => {
      if (!contact || !values ||
        !contact.contactEmail || !values.contactEmail) {
        return false;
      }

      return contact.contactEmail.toLowerCase() === values.contactEmail.toLowerCase();
    };

    const handler = this.props.handleSubmit;
    const contact = this.props.personalContacts.find((c) => c.contactId === this.props.selectedContactId);
    if (handler) {
      this.setState({
        stayAfterSave: true,
        saveFromAccountSetting: true,
      });
      handler((values, dispatch) => {
        this.onSubmit(values, dispatch, () => {
          if (successCallback) {
            successCallback(
              Object.assign(
                values, {
                  patientId: this.props.patientId,
                  contactId: this.props.selectedContactId,
                  userInfo: {
                    isEmailConfirmed: isEmailConfirmed(contact, values),
                  },
                }));
          }
        });
      })().catch(() => {
        if (errCallback) {
          errCallback();
        }
      });
    }
  }

  renderInviteCheckbox = (inviteContactChecked, isEditMode) => {
    const isNotEditMode = !isEditMode;
    if (isNotEditMode && this.inviteContactEnabled()) {
      return (
        <div className="checkbox largefont">
          <label>
            <input type="checkbox" id="checkboxAuthorize" checked={inviteContactChecked}
              onChange={this.handleOnInviteContactChange}
            />
            <FormattedMessage { ...messages.checkbox.invite } />
          </label>
        </div>
      );
    }
    return null;
  }

  renderFields = (fieldsMeta) => {
    const {
      relationships,
      patientAddress,
      contactValue,
    } = this.props;

    return (
      <div>
        { contactValue && <AccountSettings
          inviteContactEnabled={this.inviteContactEnabled()}
          contactEmail={contactValue.contactEmail}
          submitContact={this.submitContactForm}
        /> }
        <NameRelation fields={fieldsMeta} relationships={relationships}
          isPrimary={ contactValue ? contactValue.isPrimaryContact : false }
          onInputChange={ this.handleInputChange }
        />
        <AddressContainer
          fields={fieldsMeta}
          countries={this.getCountryOptions(fieldsMeta)}
          states={this.getStateOptions(fieldsMeta)}
          patientAddress={patientAddress}
          onUsePatientAddressClick={this.handleUsePatientAddressClick}
        />
        <ContactInformation
          fields={fieldsMeta}
          preferredCommunications={fieldsMeta.preferredCommunications}
          onPhoneChange={this.handleOnPhoneInputChange}
          preferredCommunicationLanguages={fieldsMeta.preferredCommunicationLanguages}
          isEditMode={fieldsMeta.isEditMode}
          defaultLanguageId={fieldsMeta.facilityDefaultLanguageId}
          languageTranslations={fieldsMeta.languageTranslations}
        />
        <div className="spacer tiny"></div>
        {this.renderInviteCheckbox(fieldsMeta.inviteContactChecked, fieldsMeta.isEditMode)}
      </div>
    );
  }

  render = () => {
    const {
      handleSubmit,
      states,
    } = this.props;

    if (states && states.length > 0) {
      return (
        <form ref={this.getContactFormRef} onSubmit={handleSubmit(this.onSubmit)}>
          <Message />
          <div className="col-xs-12" id="home-tabs-navs-contents">
            <div className="content-center-outer">
              <div className="content-center-mid">
                <div className="content-center-inner">
                  <div className="clearfix">
                    <div className="col-md-8 col-sm-12 col-md-offset-2 xsmall">
                      <Header
                        headerTitle={ this.setPageTitle() }
                      />
                    </div>
                  </div>
                  <div className="col-md-8 col-sm-12 col-md-offset-2 xsmall">
                    <div className="content-area">
                      <Fields names={fields} component={this.renderFields}
                        inviteContactChecked={this.state.inviteContactChecked}
                        isEditMode={this.props.isEditMode}
                        languageTranslations={this.props.languageTranslations}
                        preferredCommunicationLanguages={this.props.preferredCommunicationLanguages}
                        preferredCommunications={this.props.preferredCommunications}
                        facilityDefaultLanguageId={this.props.facilityDefaultLanguageId}
                      />
                      <div className="spacer small"></div>
                      <button
                        type="submit"
                        data-qa-item="save-contact"
                        data-toggle="modal"
                        data-target="#savecontact"
                        className="btn btn-lg btn-primary"
                        onClick={this.handleSubmitClick}
                      >
                        <FormattedMessage { ...messages.button.saveContact } />
                      </button>
                      &nbsp;
                      <button
                        data-qa-item="cancel_signup_step1"
                        data-toggle="modal"
                        data-target="#confirmCancel"
                        className="btn btn-lg btn-link"
                        onClick={this.handleCancelClick}
                      >
                        <FormattedMessage { ...messages.button.cancel } />
                      </button>
                      <div className="spacer medium"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      );
    }
    return null;
  }
}

Contact.propTypes = {
  dispatch: PropTypes.func,
  handleSubmit: PropTypes.func,
  patientId: PropTypes.number,
  contactId: PropTypes.number,
  relationships: PropTypes.array,
  preferredCommunications: PropTypes.array,
  onSubmitAddContact: PropTypes.func,
  countries: PropTypes.array,
  states: PropTypes.array,
  patientAddress: PropTypes.object,
  isEditMode: PropTypes.bool,
  personalContacts: PropTypes.array,
  contactValue: PropTypes.object,
  selectedContactId: PropTypes.number,
  capabilities: PropTypes.array,
  history: PropTypes.object,
  preferredCommunicationLanguages: PropTypes.array,
  facilityDefaultLanguageId: PropTypes.number,
  languageTranslations: PropTypes.object,
  languageCode: PropTypes.string,
  initialize: PropTypes.func,
};

const mapStateToProps = (state) => ({
  patientId: state.profile.patient.patientId,
  relationships: state.lookups.relationships,
  preferredCommunications: state.lookups.preferredCommunications,
  countries: state.lookups.countries,
  states: state.lookups.states,
  patientAddress: state.profile.patient.address,
  personalContacts: state.personalContacts.personalContacts,
  selectedContactId: state.personalContacts.selectedContactId,
  capabilities: state.capability.capabilities,
  preferredCommunicationLanguages: state.capability.languages,
  facilityDefaultLanguageId: state.facility.defaultCommunicationLanguageId,
  languageTranslations: state.language.languageTranslations,
  languageCode: state.language.languageCode,
});

const formConfig = {
  form: 'contactform',
  returnRejectedSubmitPromise: true,
  onSubmitFail: (errors) => scrollToFirstError(errors, fields),
  ...generateValidation(validations),
};

const contactFormWithReduxForm = reduxForm(formConfig)(Contact);
const connectedContactForm = connect(mapStateToProps)(contactFormWithReduxForm);

export default withRouter(connectedContactForm);
